import { getWorkflowParameters } from '@common/utils/execution';
import { Translation } from '@components/Flow';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { isNodeError, runWorkflow } from '@store/async/run';
import { AppDispatch } from '@store/index';
import { flowActions, tabActions } from '@store/slices';
import { WorkflowExecution, WorkflowExecutionType } from './NodeExecutionHeader';
import { EnqueueSnackbar } from 'notistack';
import { RTC } from '@components/Flow/Nodes/Trigger/InboundCall/Softphone/useRTC';

type WorkflowRunHandler = {
  service_id: string | undefined;
  workflow_id: string | undefined;
  dispatch: ThunkDispatch<AppDispatch, undefined, any>;
  translate: (workflow_id: string) => Translation;
  workflowExecution: WorkflowExecution;
  isMessageArray?: boolean;
  enqueueSnackbar: EnqueueSnackbar;
  rtc?: RTC;
};

export const runWorkflowHandler = async ({
  service_id,
  workflow_id,
  translate,
  dispatch,
  workflowExecution,
  isMessageArray = false,
  enqueueSnackbar,
  rtc,
}: WorkflowRunHandler) => {
  const { abortControllerRef, timer } = workflowExecution;

  if (timer) timer.reset();

  const workspace_id = service_id;
  const translation = translate(workflow_id || '');

  const triggerNode = translation?.nodes.find((node) => node.trigger);
  const parameters = getWorkflowParameters(triggerNode);

  dispatch(tabActions.setSelectedNode(triggerNode?.node_id || null));

  // if inbound message workflow, open message send modal
  if (workflowExecution.type === WorkflowExecutionType.InboundMessage) {
    return dispatch(flowActions.setWorkflowMessageRun(translation));
  }

  // if message array enabled, or if the workflow has parameters, open parameters modal
  if (isMessageArray) return dispatch(flowActions.setWorkflowTestRun(translation));
  if (triggerNode && parameters.length > 0 && translation) {
    return dispatch(flowActions.setWorkflowTestRun(translation));
  }

  abortControllerRef.current = new AbortController();

  if (timer) timer.start();

  try {
    const response = await dispatch(
      runWorkflow({
        workspace_id,
        workflow_id,
        parameters: { query: '', body: null, queryData: null, moduleParameters: null },
        translation,
        abortController: abortControllerRef.current,
        rtc,
      })
    );

    if (timer) timer.pause();

    if (isNodeError(response.payload)) {
      enqueueSnackbar(response.payload.error.execution.reason, { variant: 'error', autoHideDuration: 8000 });
      enqueueSnackbar('Workflow Execution Failed', { variant: 'error', autoHideDuration: 3000 });

      if (rtc?.call) {
        try {
          if (typeof rtc?.call?.hangup === 'function') rtc?.call?.hangup();
        } catch (e) {}
      }
    }
  } catch (e) {}
};
